import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { inr } from "../lib/format";
import FeatureInfoButton from "../components/FeatureInfoButton";
import { MagnifyingGlass, ShieldCheck, Warning, House, ArrowRight, Info, FileText, X } from "@phosphor-icons/react";

/**
 * PropertyHistoryPage — 40-year transaction history explorer (Phase 1 skeleton).
 *
 *   - 6 Phase-1 states (MH, KA, TS, AP, TN, UP) with district + SRO dropdowns
 *   - DPDP consent modal on first search per session
 *   - Mock mode banner when admin hasn't enabled the live Karza integration
 *   - Free tier: last 10 years, no analytics, no PDF · Pro tier: 40y + analytics
 *
 * Note: this is the Phase 1 MVP shell. Real Karza/digiverification call is
 * stubbed in property_history.py until the legal/DPA work is done.
 */
const DOC_TYPES = [
  { key: "sale", label: "Sale" },
  { key: "mortgage", label: "Mortgage" },
  { key: "gift", label: "Gift" },
  { key: "release", label: "Release" },
  { key: "partition", label: "Partition" },
];

export default function PropertyHistory() {
  const [cfg, setCfg] = useState(null);
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [sro, setSro] = useState("");
  const [ident, setIdent] = useState("");
  const [fromYear, setFromYear] = useState(1990);
  const [toYear, setToYear] = useState(new Date().getFullYear());
  const [docTypes, setDocTypes] = useState(DOC_TYPES.map((d) => d.key));
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [consentOpen, setConsentOpen] = useState(false);
  const [hasConsent, setHasConsent] = useState(
    localStorage.getItem("estima_ph_consent") === "1");
  const [pendingSearch, setPendingSearch] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/property-history/config");
        setCfg(data);
        if (data.supported_states) {
          const first = Object.keys(data.supported_states)[0];
          if (first) {
            setState(first);
            setDistrict(data.supported_states[first][0] || "");
          }
        }
      } catch (err) {
        // Config endpoint is optional — page renders an informational fallback if unreachable.
        // We still surface it in dev console for debugging.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[PropertyHistory] config fetch failed:", err?.message || err);
        }
      }
    })();
  }, []);

  const onStateChange = (s) => {
    setState(s);
    const d = cfg?.supported_states?.[s]?.[0] || "";
    setDistrict(d);
  };

  const toggleDocType = (k) => {
    setDocTypes((arr) => arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);
  };

  const runSearch = async (skipConsentGuard = false) => {
    if (!ident.trim()) {
      setError("Property identifier is required (e.g. Survey No / CTS / Flat No).");
      return;
    }
    setError("");
    if (!hasConsent && !skipConsentGuard) {
      setConsentOpen(true);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.post("/property-history/search", {
        state, district, sro, property_identifier: ident.trim(),
        from_year: Number(fromYear), to_year: Number(toYear),
        doc_types: docTypes,
      });
      setResults(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setError(detail);
      if (e?.response?.status === 403 && /consent/i.test(detail)) {
        setHasConsent(false);
        localStorage.removeItem("estima_ph_consent");
        setConsentOpen(true);
      }
    } finally { setSearching(false); }
  };

  // Trigger search once consent flips to true (avoids stale-closure on acceptConsent)
  useEffect(() => {
    if (hasConsent && pendingSearch) {
      setPendingSearch(false);
      runSearch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConsent]);

  const acceptConsent = async () => {
    try {
      await api.post("/property-history/consent", {});
      localStorage.setItem("estima_ph_consent", "1");
      setPendingSearch(true);
      setHasConsent(true);
      setConsentOpen(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not record consent");
    }
  };

  if (cfg && cfg.enabled === false && cfg.mode === "mock") {
    /* keep page accessible but mark mock mode */
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10" data-testid="property-history-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-2">India&apos;s first · 40-year property history</div>
          <h1 className="font-serif text-3xl sm:text-5xl inline-flex items-center gap-2">
            What&apos;s the story of this property? <FeatureInfoButton slug="property-history" />
          </h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Government-registered transaction history for any address in 6 Phase-1 states.
            Know how many times it changed hands, what each buyer paid, and whether the
            title is clear of unreleased mortgages.
          </p>
        </div>
      </div>

      {cfg?.mode !== "live" && (
        <div className="border border-amber-500/40 bg-amber-500/10 text-amber-700 text-xs p-3 mb-6"
          data-testid="ph-mock-banner">
          <b>Demo mode.</b> Sample results shown below. Live IGRS data is pending the legal
          opinion + Karza API integration — admin can enable from Control Room → Property History.
        </div>
      )}

      {/* Search form */}
      <div className="card-flat p-5 mb-8" data-testid="ph-search-form">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="State">
            <select value={state} onChange={(e) => onStateChange(e.target.value)}
              className="w-full bg-background border hairline px-2 py-1.5 text-xs"
              data-testid="ph-state">
              {cfg && Object.keys(cfg.supported_states || {}).map((s) =>
                <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="District / City">
            <select value={district} onChange={(e) => setDistrict(e.target.value)}
              className="w-full bg-background border hairline px-2 py-1.5 text-xs"
              data-testid="ph-district">
              {(cfg?.supported_states?.[state] || []).map((d) =>
                <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Sub-Registrar Office (optional)">
            <input value={sro} onChange={(e) => setSro(e.target.value)}
              placeholder="e.g. SRO Andheri"
              className="w-full bg-background border hairline px-2 py-1.5 text-xs"
              data-testid="ph-sro" />
          </Field>
          <Field label="Property identifier">
            <input value={ident} onChange={(e) => setIdent(e.target.value)}
              placeholder="Survey No / CTS / Flat No / Door No"
              className="w-full bg-background border hairline px-2 py-1.5 text-xs"
              data-testid="ph-ident" />
          </Field>
          <Field label="From year">
            <input type="number" value={fromYear} onChange={(e) => setFromYear(e.target.value)}
              min={1983} max={2026}
              className="w-full bg-background border hairline px-2 py-1.5 text-xs"
              data-testid="ph-from" />
          </Field>
          <Field label="To year">
            <input type="number" value={toYear} onChange={(e) => setToYear(e.target.value)}
              min={1983} max={2026}
              className="w-full bg-background border hairline px-2 py-1.5 text-xs"
              data-testid="ph-to" />
          </Field>
          <Field label="Document types">
            <div className="flex flex-wrap gap-1">
              {DOC_TYPES.map((d) => (
                <button key={d.key} onClick={() => toggleDocType(d.key)}
                  className={`px-2 py-1 text-[11px] border hairline rounded ${
                    docTypes.includes(d.key)
                      ? "bg-[hsl(var(--primary))] text-white"
                      : "hover:bg-muted/40"
                  }`}
                  data-testid={`ph-doctype-${d.key}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </Field>
          <Field label=" ">
            <button onClick={runSearch} disabled={searching}
              className="btn-primary w-full px-3 py-1.5 text-xs inline-flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid="ph-search">
              <MagnifyingGlass size={12} weight="bold" />
              {searching ? "Searching…" : "Search"}
            </button>
          </Field>
        </div>
        {error && (
          <div className="text-xs text-red-600 mt-3" data-testid="ph-error">{error}</div>
        )}
      </div>

      {/* Results */}
      {results && (
        <ResultsView results={results} />
      )}

      {/* Consent modal */}
      {consentOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="ph-consent-modal">
          <div className="bg-background border hairline max-w-md w-full p-5 space-y-3">
            <div className="eyebrow">Before you continue</div>
            <h3 className="font-serif text-2xl">Digital Personal Data Protection consent</h3>
            <p className="text-xs text-muted-foreground">
              By searching, you consent to Estima querying government IGRS / SRO portals on
              your behalf. Results are stored in your account for 90 days. Data sourced from
              public IGRS records. <b>This is not a legal Encumbrance Certificate.</b>
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setConsentOpen(false)}
                className="btn-ghost px-3 py-1.5 text-xs"
                data-testid="ph-consent-cancel">Cancel</button>
              <button onClick={acceptConsent} className="btn-primary px-3 py-1.5 text-xs"
                data-testid="ph-consent-accept">I understand and consent</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="text-xs space-y-1 block">
      <div className="text-muted-foreground">{label}</div>
      {children}
    </label>
  );
}

function ResultsView({ results }) {
  const txns = results.transactions || [];
  const a = results.analytics;
  return (
    <div className="space-y-6">
      <div className="text-xs text-muted-foreground" data-testid="ph-result-count">
        {txns.length} transaction{txns.length === 1 ? "" : "s"} found
        {results.mode && results.mode.startsWith("mock") && (
          <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-700">
            Demo data
          </span>
        )}
      </div>

      {/* Timeline */}
      {txns.length > 0 && (
        <div className="card-flat p-5" data-testid="ph-timeline">
          <div className="eyebrow mb-3">Transaction timeline</div>
          <div className="space-y-2">
            {txns.map((t, i) => (
              <div key={t.document_number || `${t.year}-${t.doc_type}-${i}`} className="flex items-baseline gap-3 border-l-2 pl-3"
                style={{ borderColor:
                  t.doc_type === "sale" ? "#0B7B6E" :
                  t.doc_type === "mortgage" ? "#D97706" :
                  t.doc_type === "gift" ? "#3B82F6" :
                  "#6B7280"
                }}
                data-testid={`ph-txn-${i}`}>
                <div className="text-sm font-serif w-16 shrink-0">{t.year}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] mr-2 ${
                      t.doc_type === "sale"     ? "bg-teal-500/15 text-teal-700" :
                      t.doc_type === "mortgage" ? "bg-amber-500/15 text-amber-700" :
                      t.doc_type === "gift"     ? "bg-blue-500/15 text-blue-700" :
                      "bg-muted/30"
                    }`}>{t.doc_type}</span>
                    {t.registered_value != null && <span className="font-medium">{inr(t.registered_value)}</span>}
                    {t.loan_amount != null && <span className="text-amber-700">Loan: {inr(t.loan_amount)}</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {t.buyer && <span>Buyer: {t.buyer} · </span>}
                    {t.seller && <span>Seller: {t.seller} · </span>}
                    {t.lender && <span>Lender: {t.lender} · </span>}
                    {t.sro} · Doc #{t.document_number}
                    {!t.is_released && t.doc_type === "mortgage" && (
                      <span className="ml-2 text-red-700">Unreleased</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics — Pro only */}
      {a ? (
        <div className="card-flat p-5" data-testid="ph-analytics">
          <div className="eyebrow mb-3">Derived analytics</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Metric k="Times changed hands" v={a.times_changed_hands || 0} />
            <Metric k="Price CAGR" v={a.price_cagr_pct != null ? `${a.price_cagr_pct}% p.a.` : "—"} />
            <Metric k="Avg holding period" v={a.avg_holding_years != null ? `${a.avg_holding_years} yrs` : "—"} />
            <Metric k="Years since last txn" v={a.years_since_last_transaction != null ? `${a.years_since_last_transaction} yrs` : "—"} />
          </div>
          <div className="mt-4">
            <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded text-xs ${
              a.encumbrance_status === "clear"
                ? "bg-green-500/15 text-green-700 border border-green-500/30"
                : "bg-red-500/15 text-red-700 border border-red-500/30"
            }`} data-testid="ph-encumbrance">
              {a.encumbrance_status === "clear" ? <ShieldCheck size={14} weight="duotone" /> : <Warning size={14} weight="duotone" />}
              {a.encumbrance_status === "clear"
                ? "Title appears clear (no unreleased mortgages)"
                : `Caution: ${a.mortgages_unreleased} unreleased mortgage(s) — verify with SRO`}
            </span>
          </div>
        </div>
      ) : (
        <div className="card-flat p-5 text-xs text-muted-foreground" data-testid="ph-analytics-locked">
          <Info size={14} weight="duotone" className="inline mr-1" />
          Derived analytics, full 40-year history, and downloadable Property Passport are part
          of <Link to="/app/pricing" className="underline text-[hsl(var(--primary))]">Estima Pro</Link>.
        </div>
      )}

      <div className="text-[10px] text-muted-foreground italic">
        Source: state IGRS portals. Data may be 24-72 hours stale. Not a legal Encumbrance
        Certificate — for a legal EC, contact your Sub-Registrar Office or property lawyer.
      </div>
    </div>
  );
}

function Metric({ k, v }) {
  return (
    <div className="border hairline p-2">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{k}</div>
      <div className="font-medium mt-0.5">{v}</div>
    </div>
  );
}
